import { IGNORED_ATTRIBUTES, LEGACY_TRACKING_PREFIXES, ECOMMERCE_EVENTS } from './config.js';
import { isProductAttributionRequired, getProductAttributionData } from '../product-attribution/productAttribution.js';
import { convertItemParameterTypes } from './utils/typeConverters.js';

/**
 * Generische Klasse zur Erstellung von GA4 DataLayer Event-Objekten nach dem
 * offiziellen GA4 Measurement Protocol Standard.
 *
 * @see https://developers.google.com/analytics/devguides/collection/protocol/ga4/reference/events
 */
export default class DataLayerEvent {

    /**
     * Erstellt ein neues DataLayer Event
     * @param {string} eventName - Name des GA4 Events (z.B. "select_item", "add_to_cart")
     * @param {Object} params - Optional: Event-Parameter
     * @param {Array<Object>} items - Optional: Items-Array
     */
    constructor(eventName, params = {}, items = null) {
        if (!eventName || typeof eventName !== 'string') {
            throw new Error('Event name is required and must be a string');
        }

        this.name = eventName;
        this.params = {};

        // Verwende nur IGNORED_ATTRIBUTES (nicht CLICK_CONTEXT_IGNORED_ATTRIBUTES)
        // CLICK_CONTEXT_IGNORED_ATTRIBUTES wird nur für HTML-Attribut-Extraktion verwendet
        this.ignoredAttributes = new Set(IGNORED_ATTRIBUTES);

        if (params && Object.keys(params).length > 0) {
            this.setParams(params);
        }

        if (items && Array.isArray(items) && items.length > 0) {
            this.setItems(items);
        }

        // Automatisch currency setzen für Ecommerce Events, falls nicht explizit in params vorhanden
        if (!this.params.currency && this._isEcommerceEvent()) {
            this.params.currency = this._getCurrencyCode();
        }
    }

    /**
     * Ermittelt den Currency-Code basierend auf der Top-Level-Domain
     * @returns {string} Currency-Code (CHF oder EUR)
     * @private
     */
    _getCurrencyCode() {
        if (typeof window === 'undefined') {
            return 'EUR'; // Fallback für Server-Side
        }

        const tld = window.location.hostname.split('.').at(-1);
        return tld === 'ch' ? 'CHF' : 'EUR';
    }

    /**
     * Entfernt Legacy-Tracking-Prefixes aus einem Attribut-Namen
     * @param {string} key - Attribut-Name
     * @returns {string} Bereinigter Attribut-Name
     * @private
     */
    _stripLegacyPrefix(key) {
        if (typeof key !== 'string') {
            return key;
        }

        for (const prefix of LEGACY_TRACKING_PREFIXES) {
            if (key.startsWith(prefix)) {
                return key.substring(prefix.length);
            }
        }

        return key;
    }

    /**
     * Prüft, ob ein Attribut ignoriert werden soll
     * @param {string} key - Attribut-Name
     * @returns {boolean} true, wenn das Attribut ignoriert werden soll
     * @private
     */
    _shouldIgnoreAttribute(key) {
        // Prüfe auf exakte Matches
        if (this.ignoredAttributes.has(key)) {
            return true;
        }

        // Prüfe auf data-* Attribute
        if (typeof key === 'string' && key.startsWith('data-')) {
            return true;
        }

        return false;
    }

    /**
     * Transformiert Bindestriche zu Unterstrichen in Attribut-Namen
     * @param {string} key - Attribut-Name
     * @returns {string} Transformierter Attribut-Name
     * @private
     */
    _transformKey(key) {
        if (typeof key !== 'string') {
            return key;
        }

        return key.replace(/-/g, '_');
    }

    /**
     * Prüft, ob das aktuelle Event productAttribution benötigt
     * Verwendet die zentrale Funktion aus product-attribution
     * @returns {boolean}
     * @private
     */
    _needsProductAttribution() {
        return isProductAttributionRequired(this.name);
    }

    /**
     * Holt die productAttribution aus dem localStorage und mappt sie auf GA4 Item-Parameter
     * Verwendet die zentrale Funktion aus product-attribution
     * @param {string} productId - Die Produkt-ID
     * @returns {Object} Gemappte Attribution-Daten oder leeres Objekt
     * @private
     */
    _getProductAttribution(productId) {
        if (typeof window === 'undefined' || !window.localStorage) {
            return {};
        }

        // getProductAttributionData behandelt Fehler bereits intern und gibt {} zurück
        const rawAttribution = getProductAttributionData(productId);
        return this._mapAttributionToGA4(rawAttribution);
    }

    /**
     * Mappt Attribution-Daten auf GA4-konforme Felder
     * @param {Object} attribution - Rohe Attribution-Daten aus localStorage
     * @returns {Object} GA4-konforme Attribution-Daten
     * @private
     */
    _mapAttributionToGA4(attribution) {
        const result = {};

        // produktkomponente → item_list_id (GA4-konform)
        if (attribution.produktkomponente) {
            result.item_list_id = attribution.produktkomponente;
        }

        // produktliste → item_list_name (GA4-konform)
        if (attribution.produktliste) {
            result.item_list_name = attribution.produktliste;
        }

        // position oder index → index (als Integer)
        if (attribution.position !== undefined) {
            result.index = parseInt(attribution.position);
        } else if (attribution.index !== undefined) {
            result.index = parseInt(attribution.index);
        }

        // Weitere Felder (seitenart, produktseite, etc.) werden NICHT gemappt
        // Diese sind nur für den Legacy newDataLayer relevant

        return result;
    }

    /**
     * Fügt einen einzelnen Parameter zum Event hinzu
     * @param {string} key - Parameter-Name
     * @param {*} value - Parameter-Wert
     * @returns {DataLayerEvent} this für Method Chaining
     */
    setParam(key, value) {
        if (value !== undefined && value !== null && !this._shouldIgnoreAttribute(key)) {
            let cleanKey = this._stripLegacyPrefix(key);
            cleanKey = this._transformKey(cleanKey);
            this.params[cleanKey] = value;
        }
        return this;
    }

    /**
     * Fügt mehrere Parameter zum Event hinzu
     * @param {Object} params - Objekt mit Event-Parametern
     * @returns {DataLayerEvent} this für Method Chaining
     */
    setParams(params) {
        if (params && typeof params === 'object') {
            Object.entries(params).forEach(([key, value]) => {
                this.setParam(key, value);
            });
        }
        return this;
    }

    /**
     * Normalisiert Item-Attribute durch Mapping alternativer Attributnamen
     * @param {Object} item - Item-Objekt zum Normalisieren
     * @returns {Object} Normalisiertes Item-Objekt
     * @private
     */
    _normalizeItem(item) {
        const normalized = { ...item };

        // Map id, pid oder product-id zu item_id (nur wenn item_id/item-id nicht existiert)
        if (!normalized.item_id && !normalized['item-id']) {
            if (normalized.id) {
                normalized.item_id = normalized.id;
                delete normalized.id;
            } else if (normalized.pid) {
                normalized.item_id = normalized.pid;
                delete normalized.pid;
            } else if (normalized['product-id']) {
                normalized.item_id = normalized['product-id'];
                delete normalized['product-id'];
            }
        }

        // Map name zu item_name (nur wenn item_name/item-name nicht existiert)
        if (!normalized.item_name && !normalized['item-name']) {
            if (normalized.name) {
                normalized.item_name = normalized.name;
                delete normalized.name;
            }
        }

        // Split category String in item_category, item_category2-5
        if (normalized.category && typeof normalized.category === 'string') {
            const categories = normalized.category.split('/').map(cat => cat.trim()).filter(cat => cat);

            if (categories.length > 0) {
                normalized.item_category = categories[0];
                if (categories.length > 1) normalized.item_category2 = categories[1];
                if (categories.length > 2) normalized.item_category3 = categories[2];
                if (categories.length > 3) normalized.item_category4 = categories[3];
                if (categories.length > 4) normalized.item_category5 = categories[4];

                // Original category Attribut entfernen
                delete normalized.category;
            }
        }

        // position → index (vor der Typ-Konvertierung)
        if (normalized.position !== undefined && normalized.position !== null && normalized.position !== '') {
            normalized.index = normalized.position;
            delete normalized.position;
        }

        // Typ-Konvertierung für alle Item-Parameter
        return convertItemParameterTypes(normalized);
    }

    /**
     * Filtert ignorierte Attribute aus einem Objekt, entfernt Legacy-Prefixes und transformiert Keys
     * @param {Object} obj - Objekt zum Filtern
     * @returns {Object} Gefiltertes Objekt ohne ignorierte Attribute und mit bereinigten Keys
     * @private
     */
    _filterIgnoredAttributes(obj) {
        const filtered = {};
        Object.entries(obj).forEach(([key, value]) => {
            if (!this._shouldIgnoreAttribute(key) && value !== undefined && value !== null) {
                let cleanKey = this._stripLegacyPrefix(key);
                cleanKey = this._transformKey(cleanKey);
                filtered[cleanKey] = value;
            }
        });
        return filtered;
    }

    /**
     * Fügt ein Item zum Items-Array hinzu
     * @param {Object} item - Item-Objekt (muss mindestens item_id oder item_name enthalten)
     * @returns {DataLayerEvent} this für Method Chaining
     */
    addItem(item) {
        if (!item || typeof item !== 'object') {
            throw new Error('Item must be an object');
        }

        if (!this.params.items) {
            this.params.items = [];
        }

        // Normalisiere Item-Attribute (Mapping für alternative Attributnamen)
        let normalizedItem = this._normalizeItem(item);

        // Filtere ignorierte Attribute aus dem Item (inkl. Prefix-Entfernung und Key-Transformation)
        let filteredItem = this._filterIgnoredAttributes(normalizedItem);

        // Validate AFTER transformation
        if (!filteredItem.item_id && !filteredItem.item_name) {
            throw new Error('Item must contain at least item_id or item_name');
        }

        // Füge productAttribution hinzu, falls erforderlich
        if (this._needsProductAttribution()) {
            // Verwende item_id oder id als Produkt-ID
            const productId = filteredItem.item_id || filteredItem.id;
            if (productId) {
                const attribution = this._getProductAttribution(productId);
                filteredItem = { ...filteredItem, ...attribution };
            }
        }

        // Kopiere item_list_id und item_list_name von Event-Level in Item (falls nicht bereits vorhanden)
        // Diese Felder sollten laut GA4 Standard in den Items sein, nicht nur auf Event-Ebene
        if (this.params.item_list_id && !filteredItem.item_list_id) {
            filteredItem.item_list_id = this.params.item_list_id;
        }
        if (this.params.item_list_name && !filteredItem.item_list_name) {
            filteredItem.item_list_name = this.params.item_list_name;
        }

        this.params.items.push(filteredItem);
        return this;
    }

    /**
     * Fügt mehrere Items zum Items-Array hinzu
     * @param {Array<Object>} items - Array von Item-Objekten
     * @returns {DataLayerEvent} this für Method Chaining
     */
    addItems(items) {
        if (!Array.isArray(items)) {
            throw new Error('Items must be an array');
        }

        items.forEach(item => this.addItem(item));
        return this;
    }

    /**
     * Setzt das komplette Items-Array (überschreibt vorhandene Items)
     * @param {Array<Object>} items - Array von Item-Objekten
     * @returns {DataLayerEvent} this für Method Chaining
     */
    setItems(items) {
        if (!Array.isArray(items)) {
            throw new Error('Items must be an array');
        }

        this.params.items = [];
        return this.addItems(items);
    }

    /**
     * Prüft, ob das aktuelle Event ein E-Commerce Event ist
     * Ein Event ist ein E-Commerce Event wenn:
     * - Es in der offiziellen E-Commerce Events Liste ist, ODER
     * - Es Items enthält (Custom E-Commerce Events)
     * @returns {boolean}
     * @private
     */
    _isEcommerceEvent() {
        return ECOMMERCE_EVENTS.includes(this.name) ||
               (this.params.items && Array.isArray(this.params.items) && this.params.items.length > 0);
    }

    /**
     * Gibt das vollständige Event-Objekt im GA4 Format zurück
     *
     * Für E-Commerce Events wird die ecommerce-Struktur verwendet:
     * {
     *   event: "select_item",
     *   ecommerce: {
     *     item_list_id: "...",
     *     items: [...]
     *   }
     * }
     *
     * Für andere Events werden Parameter direkt auf oberster Ebene platziert:
     * {
     *   event: "page_view",
     *   page_title: "...",
     *   page_location: "..."
     * }
     *
     * @returns {Object} Event-Objekt mit event und ecommerce oder direkten Parametern
     */
    toObject() {
        if (this._isEcommerceEvent()) {
            // E-Commerce Events verwenden die ecommerce-Struktur
            return {
                event: this.name,
                ecommerce: { ...this.params }
            };
        } else {
            // Andere Events haben Parameter direkt auf oberster Ebene
            return {
                event: this.name,
                ...this.params
            };
        }
    }

    /**
     * Konvertiert das Event zu JSON
     * @returns {string} JSON-String des Event-Objekts
     */
    toJSON() {
        return JSON.stringify(this.toObject());
    }
}